/* eslint-disable camelcase */
import { useMemo } from 'react';
import { getStoreRegionInfo, GetStoreRegionInfoParams } from 'services/api/store';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { regionList as AllRegions } from 'services/config/region';
import { uniq } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey';

export const useRegionMap = (params?: GetStoreRegionInfoParams) => {
  const { departmentId, departmentIds, scene } = params || {};

  const { data, isLoading } = useQuery({
    queryKey: [ApiQueryKey.StoreRegionInfo, departmentId, departmentIds, scene],
    queryFn: async () => {
      try {
        const ret = await getStoreRegionInfo({ departmentId, departmentIds, scene });
        const {
          data: { data },
        } = ret;
        // 这里加个filter防止后端给一些不应该展示的站点
        // 使用单次循环收集所有平台的 region 数据
        const result = {
          shopee: [] as string[],
          lazada: [] as string[],
          wa_biz: [] as string[],
          tokopedia: [] as string[],
          tiktok: [] as string[],
          tiktok_us: [] as string[],
          aliexpress: [] as string[],
          mercado: [] as string[],
          kwai: [] as string[],
          facebook: [] as string[],
        };

        for (const item of data) {
          const { platform, region } = item;
          switch (platform) {
            // SHOPEE 和 LAZADA 需要额外校验 region 是否在 AllRegions 中
            case 'SHOPEE':
              if (AllRegions.includes(toLowerCase(region))) {
                result.shopee.push(region);
              }
              break;
            case 'LAZADA':
              if (AllRegions.includes(toLowerCase(region))) {
                result.lazada.push(region);
              }
              break;
            case 'WA_BIZ':
              result.wa_biz.push(region);
              break;
            case 'TIKTOK':
              result.tiktok.push(region);
              break;
            case 'TIKTOK_US':
              result.tiktok_us.push(region);
              break;
            case 'ALIEXPRESS':
              result.aliexpress.push(region);
              break;
            case 'MERCADO':
              result.mercado.push(region);
              break;
            case 'KWAI':
              result.kwai.push(region);
              break;
            case 'FACEBOOK':
              result.facebook.push(region);
              break;
            case 'TOKOPEDIA':
              result.tokopedia.push(region);
              break;
          }
        }

        return result;
      } catch (err) {
        return {
          shopee: [],
          lazada: [],
          wa_biz: [],
          tokopedia: [],
          tiktok: [],
          tiktok_us: [],
          aliexpress: [],
          mercado: [],
          kwai: [],
          facebook: [],
        };
      }
    },
    staleTime: 10 * 1000,
  });

  return { data, loading: isLoading };
};

export const useRealRegionListByRegionsMap = (
  regionsMap: ReturnType<typeof useRegionMap>['data'],
  platform?: General.PlatformUpperCaseTunnel,
) => {
  const regionList = useMemo(() => {
    if (!regionsMap) {
      return [];
    }
    if (!platform) {
      return uniq(Object.values(regionsMap).flat()).filter((region) => (region as any) !== 'WORLD');
    }
    return uniq(regionsMap[toLowerCase(platform)]);
  }, [platform, regionsMap]);
  return regionList;
};

export const useRealRegionList = (
  params?: GetStoreRegionInfoParams,
  platform?: General.PlatformUpperCaseTunnel,
) => {
  const { data: regionsMap } = useRegionMap(params);
  return useRealRegionListByRegionsMap(regionsMap, platform);
};

export const usePlatformListOfRegionByRegionsMap = (
  regionsMap: ReturnType<typeof useRegionMap>['data'],
) => {
  return useMemo(() => {
    if (!regionsMap) {
      return [];
    }
    return Object.keys(regionsMap)
      .filter((platform) => regionsMap[platform].length)
      .map(toUpperCase);
  }, [regionsMap]);
};

export const usePlatformListOfRegion = (params?: GetStoreRegionInfoParams) => {
  const { data: regionsMap } = useRegionMap(params);
  return usePlatformListOfRegionByRegionsMap(regionsMap);
};

const useRegionList = (
  params?: GetStoreRegionInfoParams,
  platform?: General.PlatformUpperCaseTunnel,
) => {
  const { data: regionsMap } = useRegionMap(params);
  const platformList = usePlatformListOfRegionByRegionsMap(regionsMap);
  const regionList = useRealRegionListByRegionsMap(regionsMap, platform);
  return {
    regionList,
    regionsMap,
    platformList,
  };
};

export default useRegionList;
